import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import PageHeader from "../components/PageHeader";
import DataTable, { Column } from "../components/DataTable";
import StatTile from "../components/StatTile";
import DateRangeFilter from "../components/DateRangeFilter";
import { TagChip } from "../components/Pills";
import { CardGrid, ErrorBanner, LoadingNote, NoteCard } from "../components/Layout";
import { useDashboardData } from "../hooks/useDashboardData";
import { useDateRange } from "../hooks/useDateRange";
import { fetchPayments } from "../dashboardApi";
import { PaymentRow, PaymentsResponse, PaymentStatusFilter } from "../types";
import { colors, type } from "../styles";
import { formatCents, formatDate, formatNumber } from "../format";

/**
 * Individual Stripe payments — the ledger behind the Home tab's revenue
 * aggregates, and the last screen carried over from the legacy
 * `/adminDashboard`.
 *
 * **LIVE, and that was measured rather than assumed.** The instinct from the
 * Events screen — "a per-record list is expensive, cache it" — does not
 * transfer, because Stripe is not Firestore. `charges.search` filters
 * server-side and returns one cursor page, so its cost is FLAT in the window:
 * 3.2s over one day, 3.5s over ninety. The `charges.list` shape the nightly
 * rollup uses walks the whole range instead (25.7s at 7 days, 129.7s at 30,
 * 275.7s at 90), and that difference is the whole live-vs-cached decision.
 *
 * **Three bugs in the legacy screen this fixes, each verified against live
 * data rather than inferred from reading the code:**
 *
 * 1. Its Failed tab could never show a row. It searched `status:"succeeded"`
 *    and called a result "failed" when `metadata.eventId` was `undefined` — but
 *    Markit writes the sentinel `'N/A'` instead of omitting the key, so the
 *    test was never true. Over a 7-day window: 100 results, 96 Successful,
 *    **0 Failed**. Meanwhile 194 genuinely declined charges in a 30-day window
 *    were invisible to the screen entirely. The Failed tab here queries
 *    `status:"failed"` server-side and shows Stripe's own decline reason.
 * 2. Non-ticket payments (`eventId: 'N/A'`) passed neither tab's test and
 *    appeared in neither — silently dropped. They are listed here, with no
 *    linked event.
 * 3. Refunded payments were filtered out of Successful, while the UI carried a
 *    "Refunded" label that could never render. They are listed and marked.
 *
 * The Google Voice lookup is preserved, and only now does it have anything to
 * do: it hangs off the Failed tab's phone numbers, which the backend resolves
 * per row (`billing_details.phone` is null on every failed charge measured, so
 * this really does need Stripe's customer record).
 */

const STATUS_TABS: { key: PaymentStatusFilter; label: string }[] = [
  { key: "succeeded", label: "Successful" },
  { key: "failed", label: "Failed" },
];

const parseStatus = (raw: string | null): PaymentStatusFilter =>
  raw === "failed" ? "failed" : "succeeded";

const PaymentsScreen = () => {
  const [range, setRange] = useDateRange();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = parseStatus(searchParams.get("status"));
  const query = searchParams.get("q") ?? "";

  // Cursor pages are a stack rather than an index: Stripe's search API hands
  // back an opaque `next_page` token and has no "page N" or "previous page", so
  // going back means remembering the token that produced each page.
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];

  const fetcher = useCallback(
    () => fetchPayments(range, status, cursor),
    [range, status, cursor]
  );
  const { data, error, loading, refreshing } = useDashboardData<PaymentsResponse>(
    fetcher,
    [range, status, cursor]
  );

  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set(key, value);
      else next.delete(key);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const changeStatus = useCallback(
    (next: PaymentStatusFilter) => {
      // A cursor belongs to one query. Carrying it across a tab or range change
      // would page into a different result set from an unrelated offset.
      setCursors([]);
      setParam("status", next === "succeeded" ? "" : next);
    },
    [setParam]
  );

  const changeRange = useCallback(
    (next: Parameters<typeof setRange>[0]) => {
      setCursors([]);
      setRange(next);
    },
    [setRange]
  );

  const payments = useMemo(() => data?.payments ?? [], [data]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return payments;
    return payments.filter(payment =>
      `${payment.buyer.fullName} ${payment.buyer.username} ${payment.buyer.phoneNumber ?? ""} ${
        payment.event?.title ?? ""
      } ${payment.event?.id ?? ""} ${payment.chargeId} ${payment.paymentIntentId}`
        .toLowerCase()
        .includes(needle)
    );
  }, [payments, query]);

  const freshness = { kind: "live" as const };
  const isFailed = status === "failed";

  const timeColumn: Column<PaymentRow> = {
    key: "createdAt",
    label: "When",
    sortValue: row => row.createdAt,
    render: row => {
      const date = new Date(row.createdAt);
      return (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>
            {date.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
            })}
          </span>
          <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
            {formatDate(row.createdAt)}
          </span>
        </div>
      );
    },
  };

  const buyerColumn: Column<PaymentRow> = {
    key: "buyer",
    label: "Buyer",
    sortValue: row => row.buyer.fullName.toLowerCase(),
    render: row => (
      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <span style={{ ...type.bodyMedium, color: colors.ink }}>
          {row.buyer.fullName || "—"}
        </span>
        {isFailed ? (
          row.buyer.phoneNumber ? (
            // The Google Voice lookup, carried over from the legacy screen —
            // Markit's support number lives there, so this is how a declined
            // buyer actually gets contacted.
            <a
              href="https://voice.google.com/u/4/messages"
              target="_blank"
              rel="noopener noreferrer"
              title="Look this number up in Google Voice"
              style={{
                ...type.bodySmallRegular,
                color: colors.planPro,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              {row.buyer.phoneNumber}
              <Icon icon="ion:open-outline" height={10} />
            </a>
          ) : (
            <span
              style={{ ...type.bodySmallRegular, color: colors.inkFaint }}
              title="Stripe holds no phone number for this customer."
            >
              no phone on file
            </span>
          )
        ) : row.buyer.username ? (
          <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
            @{row.buyer.username}
          </span>
        ) : null}
      </div>
    ),
  };

  const amountColumn: Column<PaymentRow> = {
    key: "amount",
    label: "Amount",
    numeric: true,
    sortValue: row => row.amountCents,
    render: row => (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
        <span style={{ ...type.bodyMedium, color: colors.ink }}>
          {formatCents(row.amountCents, true)}
        </span>
        {row.amountRefundedCents > 0 && (
          <span
            style={{ ...type.bodySmallRegular, color: colors.stateTrial }}
            title={`${formatCents(row.amountRefundedCents, true)} refunded`}
          >
            {row.amountRefundedCents >= row.amountCents
              ? "Refunded"
              : `Partly refunded (${formatCents(row.amountRefundedCents, true)})`}
          </span>
        )}
      </div>
    ),
  };

  const eventColumn: Column<PaymentRow> = {
    key: "event",
    label: "Event",
    sortValue: row => (row.event?.title ?? "").toLowerCase(),
    render: row =>
      row.event ? (
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <a
            href={`https://markitai.com/e/${row.event.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...type.bodyMedium, color: colors.ink, textDecoration: "none" }}
            title={row.event.title || row.event.id}
          >
            {row.event.title || "(untitled)"}
          </a>
          <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
            {row.event.id}
          </span>
        </div>
      ) : (
        <span
          style={{ color: colors.inkFaint }}
          title="Not a ticket purchase — a membership, subscription or other product payment. The legacy screen dropped these rows entirely."
        >
          Not a ticket purchase
        </span>
      ),
  };

  const ticketColumn: Column<PaymentRow> = {
    key: "ticket",
    label: "Tickets",
    numeric: true,
    sortValue: row => row.ticket?.count ?? 0,
    render: row =>
      row.ticket ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <span style={{ ...type.bodyMedium, color: colors.ink }}>
            {formatNumber(row.ticket.count)}
          </span>
          {row.ticket.label && (
            <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
              {row.ticket.label}
            </span>
          )}
        </div>
      ) : (
        <span style={{ color: colors.inkFaint }}>—</span>
      ),
  };

  const declineColumn: Column<PaymentRow> = {
    key: "failure",
    label: "Decline reason",
    sortValue: row => row.failureCode ?? "",
    render: row => (
      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <span style={{ ...type.bodyMedium, color: colors.stateFailed }}>
          {row.failureMessage || "Declined"}
        </span>
        {row.failureCode && (
          <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
            {row.failureCode}
          </span>
        )}
      </div>
    ),
  };

  const stripeColumn: Column<PaymentRow> = {
    key: "stripe",
    label: "",
    render: row => (
      <a
        href={`https://dashboard.stripe.com/payments/${row.paymentIntentId || row.chargeId}`}
        target="_blank"
        rel="noopener noreferrer"
        title="Open this payment in the live Stripe dashboard"
        style={{
          ...type.bodySmallMedium,
          color: colors.inkMuted,
          textDecoration: "none",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          whiteSpace: "nowrap",
        }}
      >
        Stripe
        <Icon icon="ion:open-outline" height={11} />
      </a>
    ),
  };

  const columns: Column<PaymentRow>[] = isFailed
    ? [timeColumn, buyerColumn, amountColumn, declineColumn, eventColumn, stripeColumn]
    : [timeColumn, buyerColumn, amountColumn, eventColumn, ticketColumn, stripeColumn];

  const totals = data?.pageTotals;

  return (
    <div>
      <PageHeader
        title="Payments"
        subtitle="Individual Stripe payments — the ledger behind the Home tab's revenue totals."
      />

      {error && <ErrorBanner message={error} />}

      <div style={{ marginBottom: 16 }}>
        <DateRangeFilter value={range} onChange={changeRange} />
      </div>

      {data && totals && (
        <div style={{ marginBottom: 16 }}>
          <CardGrid min={200}>
            <StatTile
              label={isFailed ? "Declines on this page" : "Payments on this page"}
              value={formatNumber(totals.count)}
              secondary={
                data.hasMore
                  ? "More pages available — see the note below"
                  : "Every payment in this range"
              }
              freshness={freshness}
            />
            <StatTile
              label={isFailed ? "Value declined" : "Gross on this page"}
              value={formatCents(totals.grossCents)}
              secondary="Before Stripe fees and Markit's cut"
              tone={isFailed ? "warning" : "neutral"}
              freshness={freshness}
            />
            {isFailed ? (
              <StatTile
                label="Buyers reachable"
                value={formatNumber(
                  payments.filter(payment => payment.buyer.phoneNumber).length
                )}
                secondary="Have a phone number on file in Stripe"
                freshness={freshness}
              />
            ) : (
              <StatTile
                label="Refunded on this page"
                value={formatCents(totals.refundedCents)}
                secondary={
                  totals.refundedCents > 0
                    ? "Included in the gross figure beside it"
                    : "No refunds on this page"
                }
                freshness={freshness}
              />
            )}
            <StatTile
              label="Tickets sold"
              value={formatNumber(totals.ticketCount)}
              secondary="Across the ticket purchases on this page"
              freshness={freshness}
            />
          </CardGrid>
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", gap: 6 }}>
          {STATUS_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => changeStatus(tab.key)}
              style={{
                ...type.bodySmallMedium,
                color: status === tab.key ? colors.surface : colors.inkMuted,
                background: status === tab.key ? colors.ink : colors.surface,
                border: `1px solid ${status === tab.key ? colors.ink : colors.border}`,
                borderRadius: 999,
                padding: "6px 12px",
                cursor: "pointer",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <input
          value={query}
          onChange={event => setParam("q", event.target.value)}
          placeholder="Search buyer, event or payment ID"
          style={{
            ...type.bodyRegular,
            color: colors.ink,
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            padding: "8px 12px",
            minWidth: 280,
            outline: "none",
          }}
        />

        <span style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>
          {formatNumber(filtered.length)} of {formatNumber(payments.length)} on this page
          {cursors.length > 0 ? ` · page ${cursors.length + 1}` : ""}
        </span>

        {refreshing && (
          <TagChip label="Refreshing…" icon="ion:sync-outline" title="Fetching a new page" />
        )}
      </div>

      {loading && <LoadingNote />}

      {data && (
        <>
          <DataTable
            rows={filtered}
            columns={columns}
            rowKey={row => row.chargeId}
            initialSort={{ key: "createdAt", direction: "desc" }}
            emptyLabel={
              payments.length === 0
                ? isFailed
                  ? "No declined payments in this range."
                  : "No payments in this range."
                : "No payments match this search."
            }
          />

          {/* Cursor paging. Stripe's search API has no page count and no
              backward cursor, so "Previous" pops the token stack rather than
              asking Stripe for the page before this one. */}
          {(data.hasMore || cursors.length > 0) && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginTop: 16,
              }}
            >
              <button
                disabled={cursors.length === 0}
                onClick={() => setCursors(stack => stack.slice(0, -1))}
                style={{
                  ...type.bodySmallMedium,
                  color: cursors.length === 0 ? colors.inkFaint : colors.ink,
                  background: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  padding: "7px 14px",
                  cursor: cursors.length === 0 ? "default" : "pointer",
                }}
              >
                Previous
              </button>
              <button
                disabled={!data.hasMore || !data.nextCursor}
                onClick={() =>
                  data.nextCursor && setCursors(stack => [...stack, data.nextCursor!])
                }
                style={{
                  ...type.bodySmallMedium,
                  color: data.hasMore ? colors.surface : colors.inkFaint,
                  background: data.hasMore ? colors.ink : colors.surface,
                  border: `1px solid ${data.hasMore ? colors.ink : colors.border}`,
                  borderRadius: 8,
                  padding: "7px 14px",
                  cursor: data.hasMore ? "pointer" : "default",
                }}
              >
                Next 100
              </button>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <NoteCard title="These totals describe this page, not the whole range">
              Stripe&rsquo;s search API returns one cursor page of up to 100 payments
              and no total count, so a range-wide figure would have to be
              invented rather than measured — the tiles above deliberately say
              &ldquo;on this page&rdquo;. For range-wide revenue, use the Home
              tab, which is built from the nightly rollup over every charge.{" "}
              {isFailed ? (
                <>
                  <strong>Failed means Stripe declined the card</strong> — these
                  are real attempted purchases that did not go through, which the
                  legacy screen never showed: its Failed tab tested for missing
                  metadata that Markit never actually omits, so it returned zero
                  rows every time. Abandoned checkouts where no card was ever
                  entered are not counted here.
                </>
              ) : (
                <>
                  Payments with no linked event are memberships, subscriptions
                  and other product sales; the legacy screen dropped them, and
                  refunded payments, from the list entirely rather than showing
                  them.
                </>
              )}
            </NoteCard>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentsScreen;
